/* eslint-disable @typescript-eslint/no-explicit-any */
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useNavigate } from "react-router-dom";
import { 
  useFetchEquitySecurities,
  useCreateEquitySecurity, 
  useUpdateEquitySecurity, 
  useDeleteEquitySecurity,
} from '@equitySecurityHooks';
import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { 
  PageHeaderSkeleton, 
  AccountCardSkeleton 
} from '@/components/LoadingSkeletons';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowDownToLine, ArrowUpFromLine, Plus, Edit, Trash2, TrendingUp, TrendingDown, Search, DollarSign } from 'lucide-react';
import { Input } from '@/components/ui/input';
import EquityForm from "./EquityForm";
import StockPriceDisplay from "./StockPriceDisplay";
import { calculateEquityProfitLoss } from '@equityUtils';
import { SecuritiesTransactionForm, type SecuritiesTransactionFormData } from '@/components/securities-transaction-form/SecuritiesTransactionForm';
import { useFetchAccounts } from '@accountHooks';
import { useCreateSecuritiesTransaction } from '@/hooks/securities/useSecuritiesTransactions.ts';
import { SecuritiesErrorState } from '@/components/securities-error-state/SecuritiesErrorState';
import { EquityDividendForm } from '@/components/equity-dividend-form/EquityDividendForm';
import { useCreateEquityDividend } from '@equitySecurityHooks';
import type { EquityDividendFormData } from '@/types/equityDividend';
import {EquitySecurity} from "@/integrations/supabase/equity.models.ts";

/**
 * EquityPage component displays and manages equity securities (stocks).
 * This is a dedicated page for equity securities accessible at /investments/securities/equity.
 */
export default function EquityPage() {
  // Dialog state
  const [equityFormOpen, setEquityFormOpen] = useState(false);
  const [transactionFormOpen, setTransactionFormOpen] = useState(false);
  const [transactionType, setTransactionType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [editingEquity, setEditingEquity] = useState<EquitySecurity | null>(null);
  const [deletingEquityId, setDeletingEquityId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dividendFormOpen, setDividendFormOpen] = useState(false);
  const navigate = useNavigate();

  // Fetch equity securities
  const { 
    data: equitySecurities = [], 
    isLoading, 
    error,
    refetch,
  } = useFetchEquitySecurities();

  const filteredEquities = useMemo(() => {
    if (!searchQuery) return equitySecurities;
    const lowerQuery = searchQuery.toLowerCase();
    return equitySecurities.filter(e => 
      e.company_name?.toLowerCase().includes(lowerQuery) ||
      e.symbol?.toLowerCase().includes(lowerQuery) ||
      e.exchange?.toLowerCase().includes(lowerQuery) ||
      e.account_number?.toLowerCase().includes(lowerQuery) ||
      e.member_number?.toLowerCase().includes(lowerQuery)
    );
  }, [equitySecurities, searchQuery]);

  // Fetch accounts for transaction form
  const { data: accounts = [], isLoading: accountsLoading, error: accountsError } = useFetchAccounts();

  // Mutation hooks
  const createEquity = useCreateEquitySecurity();
  const updateEquity = useUpdateEquitySecurity();
  const deleteEquity = useDeleteEquitySecurity();
  const createTransaction = useCreateSecuritiesTransaction();
  const createDividend = useCreateEquityDividend();

  // Handler functions for action buttons
  const handleAddEquity = () => {
    setEditingEquity(null);
    setEquityFormOpen(true);
  };

  const handleDeposit = () => {
    setTransactionType('deposit');
    setTransactionFormOpen(true);
  };

  const handleWithdraw = () => {
    setTransactionType('withdrawal');
    setTransactionFormOpen(true);
  };

  const handleEditEquity = (equity: EquitySecurity) => {
    setEditingEquity(equity);
    setEquityFormOpen(true);
  };

  const handleDeleteEquity = (equityId: string) => {
    setDeletingEquityId(equityId);
  };

  // Handler for saving equity (create or update)
  const handleSaveEquity = async (data: any) => {
    try {
      if (editingEquity) {
        await updateEquity.mutateAsync({ id: editingEquity.id, ...data });
        toast.success('Equity security updated successfully');
        setStatusMessage('Equity security updated successfully');
      } else {
        await createEquity.mutateAsync(data);
        toast.success('Equity security created successfully');
        setStatusMessage('Equity security created successfully');
      }
      setEquityFormOpen(false);
      setEditingEquity(null);
    } catch (error) {
      console.error('Error saving equity:', error);
      const msg = editingEquity ? 'Failed to update equity security' : 'Failed to create equity security';
      toast.error(msg);
      setStatusMessage(msg);
    }
  };

  // Handler for canceling equity form
  const handleCancelEquityForm = () => {
    setEquityFormOpen(false);
    setEditingEquity(null);
  };

  // Handler for confirming delete
  const handleConfirmDelete = async () => {
    if (!deletingEquityId) return;
    
    try {
      await deleteEquity.mutateAsync(deletingEquityId);
      toast.success('Equity security deleted successfully');
      setStatusMessage('Equity security deleted successfully');
      setDeletingEquityId(null);
    } catch (error) {
      console.error('Error deleting equity:', error);
      toast.error('Failed to delete equity security');
      setStatusMessage('Failed to delete equity security');
    }
  };

  // Handler for canceling delete
  const handleCancelDelete = () => {
    setDeletingEquityId(null);
  };

  // Handler for saving transaction
  const handleSaveTransaction = async (data: SecuritiesTransactionFormData) => {
    try {
      await createTransaction.mutateAsync({
        security_type: data.security_type,
        security_id: data.security_id,
        account_id: data.account_id,
        transaction_type: data.transaction_type,
        amount: data.amount,
        currency: data.currency,
        transaction_date: data.transaction_date,
        notes: data.notes || null,
      });
      const msg = `${data.transaction_type === 'deposit' ? 'Deposit' : 'Withdrawal'} recorded successfully`;
      toast.success(msg);
      setStatusMessage(msg);
      setTransactionFormOpen(false);
    } catch (error) {
      console.error('Error recording transaction:', error);
      toast.error('Failed to record transaction');
      setStatusMessage('Failed to record transaction');
    }
  };

  // Handler for canceling transaction form
  const handleCancelTransactionForm = () => {
    setTransactionFormOpen(false);
  };

  // Handler for recording dividend
  const handleRecordDividend = () => {
    if (hasEquities && !isLoading) setDividendFormOpen(true);
  };

  const handleSaveDividend = async (data: EquityDividendFormData) => {
    try {
      await createDividend.mutateAsync({ formData: data });
      setDividendFormOpen(false);
    } catch {
      // Error handling (toasts) is managed inside useCreateEquityDividend.
      // Do not close the modal on failure so the user can retry.
    }
  };

  const handleCancelDividend = () => {
    setDividendFormOpen(false);
  };

  // Disable deposit/withdraw buttons when no equities exist
  const hasEquities = equitySecurities.length > 0;

  // Prepare securities list for transaction form
  const securitiesForTransaction = equitySecurities.map(equity => ({
    id: equity.id,
    label: `${equity.symbol} - ${equity.company_name}`,
    currency: equity.currency,
  }));

  // Show loading skeletons while data is loading
  if (isLoading) {
    return (
      <ErrorBoundary
        onReset={() => {
          window.location.reload();
        }}
      >
        <div className="space-y-6" aria-busy="true" aria-live="polite">
          <PageHeaderSkeleton />
          <div className="space-y-4">
            <AccountCardSkeleton />
            <AccountCardSkeleton />
            <AccountCardSkeleton />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  // Show error state
  if (error) {
    return (
      <ErrorBoundary onReset={() => { window.location.reload(); }}>
        <div className="space-y-6">
          <SecuritiesErrorState
            message="Failed to load equity securities. Please try again."
            onRetry={() => refetch()}
          />
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      onReset={() => {
        window.location.reload();
      }}
    >
      <div className="space-y-6">
        {/* Screen reader live region for mutation status announcements */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {statusMessage}
        </div>

        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Equity Securities</h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              Manage your stock holdings
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-wrap w-full lg:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search equities..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row w-full sm:w-auto" role="group" aria-label="Equity securities actions">
              <Button
                onClick={handleDeposit}
                disabled={!hasEquities}
                variant="outline"
                aria-label="Deposit funds to equity account"
                aria-disabled={!hasEquities}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <ArrowDownToLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Deposit
              </Button>
              <Button
                onClick={handleWithdraw}
                disabled={!hasEquities}
                variant="outline"
                aria-label="Withdraw funds from equity account"
                aria-disabled={!hasEquities}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <ArrowUpFromLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Withdraw
              </Button>
              <Button
                onClick={handleRecordDividend}
                disabled={!hasEquities || isLoading}
                variant="outline"
                aria-label="Record dividend payment"
                aria-disabled={!hasEquities || isLoading}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <DollarSign className="mr-2 h-4 w-4" aria-hidden="true" />
                Record Dividend
              </Button>
              <Button
                onClick={handleAddEquity}
                aria-label="Add new equity security"
                className="min-h-[44px] w-full sm:w-auto"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add
              </Button>
            </div>
          </div>
        </div>

        {/* Equity Cards List or Empty State */}
        {hasEquities ? (
          <div 
            className="space-y-4" 
            role="list" 
            aria-label="Equity securities"
            aria-live="polite"
          >
            {filteredEquities.map((equity) => {
              const profitLoss = calculateEquityProfitLoss(
                equity.shares_owned,
                equity.purchase_price,
                equity.current_price
              );
              const isProfit = profitLoss.profitLoss >= 0;

              return (
                <Card key={equity.id} className="hover:bg-accent/50 transition-colors" onClick={() => navigate(`/investments/securities/equity/${equity.id}`)} >
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col gap-4">
                      {/* Header Row */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold">{equity.symbol}</h3>
                            <span className="text-sm text-muted-foreground">•</span>
                            <span className="text-sm text-muted-foreground">{equity.exchange}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{equity.company_name}</p>
                          <StockPriceDisplay symbol={equity.symbol} exchange={equity.exchange} />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditEquity(equity)}
                            aria-label={`Edit ${equity.symbol}`}
                            className="min-h-[44px] min-w-[44px]"
                          >
                            <Edit className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteEquity(equity.id)}
                            aria-label={`Delete ${equity.symbol}`}
                            className="min-h-[44px] min-w-[44px]"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </div>

                      {/* Metrics Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Shares Owned</p>
                          <p className="text-sm font-medium">{equity.shares_owned.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Purchase Price</p>
                          <p className="text-sm font-medium">{equity.currency} {equity.purchase_price.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Current Value</p>
                          <p className="text-sm font-medium">{equity.currency} {profitLoss.currentValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Profit/Loss</p>
                          <div className="flex items-center gap-1">
                            <p className={`text-sm font-medium ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                              {isProfit ? '+' : ''}{equity.currency} {profitLoss.profitLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                            {isProfit ? (
                              <TrendingUp className="h-3 w-3 text-emerald-600" aria-hidden="true" />
                            ) : (
                              <TrendingDown className="h-3 w-3 text-red-600" aria-hidden="true" />
                            )}
                          </div>
                          <p className={`text-xs ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                            {isProfit ? '+' : ''}{profitLoss.profitLossPercentage.toFixed(2)}%
                          </p>
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                        <span>Purchased: {new Date(equity.purchase_date).toLocaleDateString()}</span>
                        {equity.notes && <span>• {equity.notes}</span>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card role="status" aria-label="Empty state">
            <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <p className="text-base sm:text-lg text-muted-foreground mb-4">
                No equity securities yet
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                Get started by adding your first stock holding
              </p>
              <Button 
                onClick={handleAddEquity} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Add your first equity security"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add Investment
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Equity Dialog */}
        <Dialog open={equityFormOpen} onOpenChange={setEquityFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="equity-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {editingEquity ? 'Edit Equity Security' : 'Add Equity Security'}
              </DialogTitle>
              <DialogDescription id="equity-dialog-description">
                {editingEquity 
                  ? 'Update the details of your stock holding.' 
                  : 'Add a new stock holding to your portfolio.'}
              </DialogDescription>
            </DialogHeader>
            <EquityForm
              holding={editingEquity}
              onSave={handleSaveEquity}
              onCancel={handleCancelEquityForm}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingEquityId} onOpenChange={(open) => !open && handleCancelDelete()}>
          <AlertDialogContent 
            className="w-[95vw] sm:w-full sm:max-w-[500px]"
            aria-describedby="delete-dialog-description"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription id="delete-dialog-description">
                This action cannot be undone. This will permanently delete the equity security
                and all associated transaction history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel 
                onClick={handleCancelDelete} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Cancel deletion"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                aria-label="Confirm deletion of equity security"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Transaction Dialog */}
        <Dialog open={transactionFormOpen} onOpenChange={setTransactionFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="transaction-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {transactionType === 'deposit' ? 'Deposit to Equity Account' : 'Withdraw from Equity Account'}
              </DialogTitle>
              <DialogDescription id="transaction-dialog-description">
                {transactionType === 'deposit' 
                  ? 'Record a deposit to your equity securities account.' 
                  : 'Record a withdrawal from your equity securities account.'}
              </DialogDescription>
            </DialogHeader>
            <SecuritiesTransactionForm
              transactionType={transactionType}
              securityType="equity"
              securities={securitiesForTransaction}
              accounts={accounts}
              onSave={handleSaveTransaction}
              onCancel={handleCancelTransactionForm}
            />
          </DialogContent>
        </Dialog>

        {/* Record Dividend Dialog */}
        {dividendFormOpen && equitySecurities.length > 0 && (
          <Dialog open={dividendFormOpen} onOpenChange={setDividendFormOpen}>
            <DialogContent
              className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
              aria-describedby="dividend-dialog-description"
            >
              <DialogHeader>
                <DialogTitle>Record Dividend</DialogTitle>
                <DialogDescription id="dividend-dialog-description">
                  Record a dividend payment for one of your equity securities.
                </DialogDescription>
              </DialogHeader>
              <EquityDividendForm
                equity={equitySecurities[0]}
                accounts={accounts}
                accountsLoading={accountsLoading}
                accountsError={!!accountsError}
                onSave={handleSaveDividend}
                onCancel={handleCancelDividend}
                isSubmitting={createDividend.isPending}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </ErrorBoundary>
  );
}
